import React, { useMemo } from "react";

import SingleSelectDropdown from "../../widgets/Dropdown/SingleSelectDropdown";
import SearchCard from "../../widgets/SearchCard/SearchCard";
import VerticalRegistry from "../../constants/verticalRegistry";
import BranchSelector from "./components/BranchSelector/BranchSelector";
import useManageStudentForm from "./hooks/useManageStudentForm";
import { VERTICAL_NAMES, SEARCH_CARD_CONFIG } from "../../constants/constants";
import { isLearnOnlyRegion } from "../../constants/omsRegionConfig";
import { useCountryIsdList } from "../../services/mdsService";
import styles from "./ManageStudent.module.scss";


const getCountryIdFromIsdCode = (isdCode, countryIsdList) =>
  countryIsdList.find((country) => country.isd_code === isdCode)?.country_id ??
  null;


const ManageStudent = ({
  values,
  handleChange,
  submitForm,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  setFieldValue,
  setValues,
  selectedBranch,
  setSelectedBranch,
  fetchSchoolBranches,
}) => {
  const { searchConfig, activeCard, handleVerticalChange, handleTabChange } =
    useManageStudentForm(
      { values, handleChange, setFieldValue },
      setSelectedBranch,
    );

  const { data: countryIsdList = [] } = useCountryIsdList();

  const countryMenuItems = useMemo(
    () =>
      [...countryIsdList]
        .map((country) => ({
          value: String(country.country_id),
          label: country.display_name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [countryIsdList],
  );

  const searchCountryId = useMemo(() => {
    if (activeCard === "phone") {
      return getCountryIdFromIsdCode(values.isd_code, countryIsdList);
    }
    return values.country_id ? Number(values.country_id) : null;
  }, [activeCard, values.isd_code, values.country_id, countryIsdList]);

  const verticalOptions = useMemo(() => {
    const regionOptions = {
      countryId: searchCountryId,
      isdCode: activeCard === "phone" ? values.isd_code : undefined,
    };

    return VerticalRegistry.getVerticalsForRegion(regionOptions).map(
      (vertical) => vertical.name,
    );
  }, [searchCountryId, activeCard, values.isd_code]);

  const effectiveVertical = useMemo(() => {
    if (verticalOptions.length === 1) {
      return verticalOptions[0];
    }
    return verticalOptions.includes(values.vertical) ? values.vertical : "";
  }, [verticalOptions, values.vertical]);

  const isILSchools = effectiveVertical === VERTICAL_NAMES.IL_SCHOOLS;
  const learnOnly = isLearnOnlyRegion({
    countryId: searchCountryId,
    isdCode: activeCard === "phone" ? values.isd_code : undefined,
  });

  const handleCountryAwareVerticalChange = (event) => {
    if (learnOnly && event.target.value !== VERTICAL_NAMES.IL) {
      return;
    }
    handleVerticalChange(event);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const nextValues = {
      ...values,
      vertical: effectiveVertical || values.vertical,
      country_id:
        searchCountryId != null
          ? String(searchCountryId)
          : values.country_id,
    };
    await setValues(nextValues);
    await submitForm();
  };

  return (
    <div className={styles.manageStudentWrapper}>
      <div className={styles.card}>
        <form onSubmit={handleFormSubmit}>
          <div className={styles.topFilters}>
            <SingleSelectDropdown
              label="Select Business Vertical"
              name="vertical"
              value={effectiveVertical}
              onChange={handleCountryAwareVerticalChange}
              options={verticalOptions}
            />

            {isILSchools && (
              <BranchSelector
                errors={errors}
                selectedBranch={selectedBranch}
                setSelectedBranch={setSelectedBranch}
                setValues={setValues}
                fetchSchoolBranches={fetchSchoolBranches}
              />
            )}
          </div>

          <SearchCard
            tabs={SEARCH_CARD_CONFIG}
            activeCard={activeCard}
            setActiveCard={handleTabChange}
            config={searchConfig}
            values={values}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            isLoading={isSubmitting}
            countryMenuItems={countryMenuItems}
          />
        </form>
      </div>
    </div>
  );
};

export default ManageStudent;
